import {
  CustomOrderType,
  OrderMethod,
} from '@/pages/camel-order/sites/components/OrderMethodDisplayNameSettingFormItem/type';
import { siteForm } from '@/pages/camel-order/sites/service';
import {
  FormError,
  validAlphabetFormat,
  validAsciiFormat,
  validatePhoneNumberFormat,
  validBlank,
  validEmailFormat,
  validHalfWidthFormat,
  validKana,
  validMaxNum,
  validMaxStr,
  validMinNum,
  validNumericFormat,
  validOverStartHours,
  validRegexFormat,
  validUrlFormat,
} from '@/validate';
import { validNum } from '@/validate/number';

const { MAX_70, MAX_80, MAX_200, MAX_600 } = validNum;

export type SiteFormError = {
  title: FormError;
  description: FormError;
  password: FormError;
  notificationText: FormError;
  faqUrl: FormError;
  termUrl: FormError;
  inquiryUrl: FormError;
  sctlUrl: FormError;
  privacyUrl: FormError;
  customFieldName: FormError;
  customFieldDescription: FormError;
  customFieldMinimum: FormError;
  customFieldMaximum: FormError;
  customFieldRegex: FormError;
  deliveryMinimumAmount: FormError;
  gmoShopName: FormError;
  gmoShopNameKana: FormError;
  gmoShopNameAlphabet: FormError;
  gmoShopNameShort: FormError;
  gmoShopContactName: FormError;
  gmoShopContactEmail: FormError;
  gmoShopContactPhone: FormError;
  gmoShopContactOpeningHours: FormError;
  gmoShopContactClosingHours: FormError;
  orderTypeDisplayNameByTakeoutText: FormError;
  orderTypeDisplayNameByTakeoutButtonText: FormError;
  orderTypeDisplayNameByEatInText: FormError;
  orderTypeDisplayNameByEatInButtonText: FormError;
  orderTypeDisplayNameByDeliveryText: FormError;
  orderTypeDisplayNameByDeliveryButtonText: FormError;
};

export const validateTitle = (
  title: string,
  error: SiteFormError
): SiteFormError => {
  error.title.isTouched = true;
  const label = siteForm.title.label;
  const isMax = validMaxStr.check(title, MAX_70);
  const isBlank = validBlank.check(title);
  if (isBlank) error.title.message = validBlank.message(label);
  if (isMax) error.title.message = validMaxStr.message(label, MAX_70);
  error.title.isError = isBlank || isMax;
  return error;
};

export const validateDescription = (
  description: string,
  error: SiteFormError
): SiteFormError => {
  error.description.isTouched = true;
  const MAX_NUM = 320;
  const label = siteForm.description.label;
  const isMax = validMaxStr.check(description, MAX_NUM);
  const isBlank = validBlank.check(description);
  if (isBlank) error.description.message = validBlank.message(label);
  if (isMax) error.description.message = validMaxStr.message(label, MAX_NUM);
  error.description.isError = isBlank || isMax;
  return error;
};

export const validatePassword = (
  password: string,
  error: SiteFormError
): SiteFormError => {
  error.password.isTouched = true;
  const MAX_NUM = 30;
  const label = siteForm.password.label;
  const isMax = validMaxStr.check(password, MAX_NUM);
  const isASCII = validAsciiFormat.check(password);
  if (isMax) error.password.message = validMaxStr.message(label, MAX_NUM);
  if (!isASCII) error.password.message = validAsciiFormat.message(label);
  error.password.isError = !isASCII || isMax;
  return error;
};

export const validateNotificationText = (
  notificationText: string,
  error: SiteFormError
): SiteFormError => {
  error.notificationText.isTouched = true;
  const MAX_NUM = 1000;
  const label = siteForm.notificationText.label;
  const isMax = validMaxStr.check(notificationText, MAX_NUM);
  if (isMax)
    error.notificationText.message = validMaxStr.message(label, MAX_NUM);
  error.notificationText.isError = isMax;
  return error;
};

export const validateFaQUrl = (
  faqUrl: string,
  error: SiteFormError
): SiteFormError => {
  error.faqUrl.isTouched = true;
  const label = siteForm.faqUrl.label;
  const isUrlValid = validUrlFormat.check(faqUrl);
  if (isUrlValid) error.faqUrl.message = validUrlFormat.message(label);
  error.faqUrl.isError = isUrlValid;
  return error;
};

export const validateInquiryUrl = (
  inquiryUrl: string,
  error: SiteFormError
): SiteFormError => {
  error.inquiryUrl.isTouched = true;
  const label = siteForm.inquiryUrl.label;
  const isUrlValid = validUrlFormat.check(inquiryUrl);
  if (isUrlValid) error.inquiryUrl.message = validUrlFormat.message(label);
  error.inquiryUrl.isError = isUrlValid;
  return error;
};

export const validateTermUrl = (
  termUrl: string,
  error: SiteFormError
): SiteFormError => {
  error.termUrl.isTouched = true;
  const label = siteForm.termUrl.label;
  const isUrlValid = validUrlFormat.check(termUrl);
  const isBlank = validBlank.check(termUrl);
  if (isUrlValid) error.termUrl.message = validUrlFormat.message(label);
  if (isBlank) error.termUrl.message = validBlank.message(label);
  error.termUrl.isError = isBlank || isUrlValid;
  return error;
};

export const validateSctlUrl = (
  sctlUrl: string,
  error: SiteFormError
): SiteFormError => {
  error.sctlUrl.isTouched = true;
  const label = siteForm.sctlUrl.label;
  const isUrlValid = validUrlFormat.check(sctlUrl);
  const isBlank = validBlank.check(sctlUrl);
  if (isUrlValid) error.sctlUrl.message = validUrlFormat.message(label);
  if (isBlank) error.sctlUrl.message = validBlank.message(label);
  error.sctlUrl.isError = isBlank || isUrlValid;
  return error;
};

export const validatePrivacyUrl = (
  privacyUrl: string,
  error: SiteFormError
): SiteFormError => {
  error.privacyUrl.isTouched = true;
  const label = siteForm.privacyUrl.label;
  const isUrlValid = validUrlFormat.check(privacyUrl);
  const isBlank = validBlank.check(privacyUrl);
  if (isUrlValid) error.privacyUrl.message = validUrlFormat.message(label);
  if (isBlank) error.privacyUrl.message = validBlank.message(label);
  error.privacyUrl.isError = isBlank || isUrlValid;
  return error;
};

export const validateGmoShopName = (
  gmoShopName: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopName.isTouched = true;
  const MAX_NUM = 15;
  const label = siteForm.gmoShopName.label;
  const isMax = validMaxStr.check(gmoShopName, MAX_NUM);
  const isBlank = validBlank.check(gmoShopName);
  const isHalfWidth = validHalfWidthFormat.check(gmoShopName);
  if (isHalfWidth)
    error.gmoShopName.message = validHalfWidthFormat.message(label);
  if (isBlank) error.gmoShopName.message = validBlank.message(label);
  if (isMax) error.gmoShopName.message = validMaxStr.message(label, MAX_NUM);

  error.gmoShopName.isError = isBlank || isMax || isHalfWidth;
  return error;
};

export const validateGmoShopNameKana = (
  gmoShopNameKana: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopNameKana.isTouched = true;
  const MAX_NUM = 20;
  const label = siteForm.gmoShopNameKana.label;
  const isMax = validMaxStr.check(gmoShopNameKana, MAX_NUM);
  const isBlank = validBlank.check(gmoShopNameKana);
  const isHalfWidth = validHalfWidthFormat.check(gmoShopNameKana);
  const isNotKana = validKana.check(gmoShopNameKana);
  if (isNotKana) {
    error.gmoShopNameKana.message = validKana.message(label);
  }
  if (isHalfWidth)
    error.gmoShopNameKana.message = validHalfWidthFormat.message(label);
  if (isBlank) error.gmoShopNameKana.message = validBlank.message(label);
  if (isMax)
    error.gmoShopNameKana.message = validMaxStr.message(label, MAX_NUM);
  error.gmoShopNameKana.isError = isBlank || isMax || isHalfWidth || isNotKana;
  return error;
};

export const validateGmoShopNameAlphabet = (
  gmoShopNameAlphabet: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopNameAlphabet.isTouched = true;
  const MAX_NUM = 25;
  const label = siteForm.gmoShopNameAlphabet.label;
  const isMax = validMaxStr.check(gmoShopNameAlphabet, MAX_NUM);
  const isBlank = validBlank.check(gmoShopNameAlphabet);
  const invalidAlphabet = validAlphabetFormat.check(gmoShopNameAlphabet);
  if (invalidAlphabet)
    error.gmoShopNameAlphabet.message = validAlphabetFormat.message(label);
  if (isBlank) error.gmoShopNameAlphabet.message = validBlank.message(label);
  if (isMax)
    error.gmoShopNameAlphabet.message = validMaxStr.message(label, MAX_NUM);
  error.gmoShopNameAlphabet.isError = isBlank || isMax || invalidAlphabet;
  return error;
};

export const validateGmoShopNameShort = (
  gmoShopNameShort: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopNameShort.isTouched = true;
  const MAX_NUM = 15;
  const label = siteForm.gmoShopNameShort.label;
  const isMax = validMaxStr.check(gmoShopNameShort, MAX_NUM);
  const isBlank = validBlank.check(gmoShopNameShort);
  const isHalfWidth = validHalfWidthFormat.check(gmoShopNameShort);
  if (isHalfWidth)
    error.gmoShopNameShort.message = validHalfWidthFormat.message(label);
  if (isBlank) error.gmoShopNameShort.message = validBlank.message(label);
  if (isMax)
    error.gmoShopNameShort.message = validMaxStr.message(label, MAX_NUM);
  error.gmoShopNameShort.isError = isBlank || isMax || isHalfWidth;
  return error;
};

export const validateGmoShopContactName = (
  gmoShopContactName: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopContactName.isTouched = true;
  const MAX_NUM = 21;
  const label = siteForm.gmoShopContactName.label;
  const isMax = validMaxStr.check(gmoShopContactName, MAX_NUM);
  const isBlank = validBlank.check(gmoShopContactName);
  const isHalfWidth = validHalfWidthFormat.check(gmoShopContactName);
  if (isHalfWidth)
    error.gmoShopContactName.message = validHalfWidthFormat.message(label);
  if (isBlank) error.gmoShopContactName.message = validBlank.message(label);
  if (isMax)
    error.gmoShopContactName.message = validMaxStr.message(label, MAX_NUM);
  error.gmoShopContactName.isError = isBlank || isMax || isHalfWidth;
  return error;
};

export const validateGmoShopContactEmail = (
  gmoShopContactEmail: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopContactEmail.isTouched = true;
  const label = siteForm.gmoShopContactEmail.label;
  const isBlank = validBlank.check(gmoShopContactEmail);
  const invalidEmail = validEmailFormat.check(gmoShopContactEmail);
  if (invalidEmail)
    error.gmoShopContactEmail.message = validEmailFormat.message(label);
  if (isBlank) error.gmoShopContactEmail.message = validBlank.message(label);

  error.gmoShopContactEmail.isError = isBlank || invalidEmail;
  return error;
};

export const validateGmoShopContactPhone = (
  gmoShopContactPhone: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopContactPhone.isTouched = true;

  const label = siteForm.gmoShopContactPhone.label;
  const invalidPhone = validatePhoneNumberFormat.check(gmoShopContactPhone);
  const isBlank = validBlank.check(gmoShopContactPhone);
  if (invalidPhone)
    error.gmoShopContactPhone.message =
      validatePhoneNumberFormat.message(label);
  if (isBlank) error.gmoShopContactPhone.message = validBlank.message(label);

  error.gmoShopContactPhone.isError = isBlank || invalidPhone;
  return error;
};

export const validateGmoShopContactOpeningAndClosingHours = (
  gmoShopContactOpeningHours: string,
  gmoShopContactClosingHours: string,
  error: SiteFormError
): SiteFormError => {
  error.gmoShopContactOpeningHours.isTouched = true;
  error.gmoShopContactClosingHours.isTouched = true;
  const label = siteForm.gmoShopContactOpeningHours.label;

  const invalid = validOverStartHours.check(
    gmoShopContactOpeningHours,
    gmoShopContactClosingHours
  );

  if (invalid)
    error.gmoShopContactOpeningHours.message =
      validOverStartHours.message(label);
  error.gmoShopContactOpeningHours.isError = invalid;
  return error;
};

export const validateLinkUrl = (linkUrl: string): boolean => {
  const isUrlValid = validUrlFormat.check(linkUrl);
  const isBlank = validBlank.check(linkUrl);
  const isError = isBlank || isUrlValid;
  return isError;
};

const setOrderTypeDisplayNameError = (
  orderMethod: OrderMethod,
  customOrderType: CustomOrderType,
  error: SiteFormError,
  message: string,
  isError: boolean
) => {
  switch (orderMethod) {
    case 'takeout':
      if (customOrderType === 'text') {
        error.orderTypeDisplayNameByTakeoutText.message = message;
        error.orderTypeDisplayNameByTakeoutText.isError = isError;
      } else {
        error.orderTypeDisplayNameByTakeoutButtonText.message = message;
        error.orderTypeDisplayNameByTakeoutButtonText.isError = isError;
      }
      break;
    case 'eat_in':
      if (customOrderType === 'text') {
        error.orderTypeDisplayNameByEatInText.message = message;
        error.orderTypeDisplayNameByEatInText.isError = isError;
      } else {
        error.orderTypeDisplayNameByEatInButtonText.message = message;
        error.orderTypeDisplayNameByEatInButtonText.isError = isError;
      }
      break;
    case 'delivery':
      if (customOrderType === 'text') {
        error.orderTypeDisplayNameByDeliveryText.message = message;
        error.orderTypeDisplayNameByDeliveryText.isError = isError;
      } else {
        error.orderTypeDisplayNameByDeliveryButtonText.message = message;
        error.orderTypeDisplayNameByDeliveryButtonText.isError = isError;
      }
      break;
  }
  return error;
};

export const validateOrderTypeDisplayName = (
  orderTypeDisplayName: string,
  customOrderType: CustomOrderType,
  orderMethod: OrderMethod,
  isCustom: boolean,
  error: SiteFormError
): SiteFormError => {
  if (customOrderType === 'text' && orderMethod === 'takeout') {
    error.orderTypeDisplayNameByTakeoutText.isTouched = true;
  } else if (customOrderType === 'button_text' && orderMethod === 'takeout') {
    error.orderTypeDisplayNameByTakeoutButtonText.isTouched = true;
  } else if (customOrderType === 'text' && orderMethod === 'eat_in') {
    error.orderTypeDisplayNameByEatInText.isTouched = true;
  } else if (customOrderType === 'button_text' && orderMethod === 'eat_in') {
    error.orderTypeDisplayNameByEatInButtonText.isTouched = true;
  } else if (customOrderType === 'text' && orderMethod === 'delivery') {
    error.orderTypeDisplayNameByDeliveryText.isTouched = true;
  } else if (customOrderType === 'button_text' && orderMethod === 'delivery') {
    error.orderTypeDisplayNameByDeliveryButtonText.isTouched = true;
  }
  if (!isCustom) {
    return setOrderTypeDisplayNameError(
      orderMethod,
      customOrderType,
      error,
      '',
      false
    );
  }
  const TEXT_MAX_NUM = 10;
  const BUTTON_TEXT_MAX_NUM = 15;
  const max = customOrderType === 'text' ? TEXT_MAX_NUM : BUTTON_TEXT_MAX_NUM;
  const label = customOrderType === 'text' ? '項目名' : 'ボタンテキスト';
  const isMax = validMaxStr.check(orderTypeDisplayName, max);
  const isBlank = validBlank.check(orderTypeDisplayName);
  if (isBlank)
    return setOrderTypeDisplayNameError(
      orderMethod,
      customOrderType,
      error,
      validBlank.message(label),
      true
    );

  if (isMax)
    return setOrderTypeDisplayNameError(
      orderMethod,
      customOrderType,
      error,
      validMaxStr.message(label, max),
      true
    );

  return setOrderTypeDisplayNameError(
    orderMethod,
    customOrderType,
    error,
    '',
    false
  );
};

export const validateCustomFieldName = (
  name: string,
  error: SiteFormError
): SiteFormError => {
  error.customFieldName.isTouched = true;
  const label = siteForm.customFieldName.label;
  const isMax = validMaxStr.check(name, MAX_80);
  const isBlank = validBlank.check(name);
  if (isBlank) error.customFieldName.message = validBlank.message(label);
  if (isMax) error.customFieldName.message = validMaxStr.message(label, MAX_80);
  error.customFieldName.isError = isBlank || isMax;
  return error;
};

export const validateCustomFieldDescription = (
  description: string,
  error: SiteFormError
): SiteFormError => {
  error.customFieldDescription.isTouched = true;
  const label = siteForm.customFieldDescription.label;
  const isMax = validMaxStr.check(description, MAX_200);
  const isBlank = validBlank.check(description);
  if (isBlank) error.customFieldDescription.message = validBlank.message(label);
  if (isMax)
    error.customFieldDescription.message = validMaxStr.message(label, MAX_200);
  error.customFieldDescription.isError = isBlank || isMax;
  return error;
};

export const validateCustomFieldMinimum = (
  minimum: string,
  error: SiteFormError,
  maxMum?: number
): SiteFormError => {
  error.customFieldMinimum.isTouched = true;
  const label = siteForm.customFieldMinimum.label;
  const isBlank = validBlank.check(minimum);
  const isNumericFormat = !isBlank && validNumericFormat.check(minimum); // 空文字でない場合のみ判定
  const isValidMin = !isBlank && validMinNum.check(Number(minimum), 1);
  const maxCount = maxMum ? maxMum - 1 : 0;
  const isValidMax = maxMum
    ? !isBlank && validMaxNum.check(Number(minimum), maxCount)
    : false;
  if (isNumericFormat)
    error.customFieldMinimum.message = validNumericFormat.message(label);
  if (isValidMin)
    error.customFieldMinimum.message = validMinNum.message(label, 1);
  if (isValidMax)
    error.customFieldMinimum.message = validMaxNum.message(label, maxCount);
  error.customFieldMinimum.isError =
    isNumericFormat || isValidMin || isValidMax;
  return error;
};

export const validateCustomFieldMaximum = (
  maximum: string,
  error: SiteFormError,
  minMum?: number
): SiteFormError => {
  error.customFieldMaximum.isTouched = true;
  const label = siteForm.customFieldMaximum.label;
  const isBlank = validBlank.check(maximum);
  const isNumericFormat = !isBlank && validNumericFormat.check(maximum);
  const isValidMin = minMum
    ? !isBlank && validMinNum.check(Number(maximum), minMum + 1)
    : false;
  const isAboveMax = validMaxNum.check(Number(maximum), MAX_600);
  if (isNumericFormat)
    error.customFieldMaximum.message = validNumericFormat.message(label);
  if (isAboveMax)
    error.customFieldMaximum.message = validMaxNum.message(label, MAX_600);
  if (isValidMin)
    error.customFieldMaximum.message = validMinNum.message(
      label,
      minMum as number
    );
  error.customFieldMaximum.isError =
    isAboveMax || isNumericFormat || isValidMin;
  return error;
};

export const validateCustomFieldRegex = (
  regex: string,
  error: SiteFormError
): SiteFormError => {
  error.customFieldRegex.isTouched = true;
  const label = siteForm.customFieldRegex.label;
  const isRegex = validRegexFormat.check(regex);
  if (isRegex) error.customFieldRegex.message = validRegexFormat.message(label);
  error.customFieldRegex.isError = isRegex;
  return error;
};

export const validateDeliveryMinimumAmount = (
  deliveryMinimumAmount: string,
  error: SiteFormError
): SiteFormError => {
  error.deliveryMinimumAmount.isTouched = true;
  const label = siteForm.deliveryMinimumAmount.label;
  const amount =
    deliveryMinimumAmount.trim() === '' ? '0' : deliveryMinimumAmount;
  const isNumericFormat = validNumericFormat.check(amount);
  const isValidMin = validMinNum.check(Number(amount), 0);
  const isValidMax = validMaxNum.check(Number(amount), 9999999);
  if (isNumericFormat)
    error.deliveryMinimumAmount.message = validNumericFormat.message(label);
  if (isValidMin)
    error.deliveryMinimumAmount.message = validMinNum.message(label, 0);
  if (isValidMax)
    error.deliveryMinimumAmount.message = validMaxNum.message(label, 9999999);
  error.deliveryMinimumAmount.isError =
    isNumericFormat || isValidMin || isValidMax;
  return error;
};
